// เหตุการณ์ที่ใช้ได้กับทุก archetype (docs/PLAN.md ข้อ 3.7)

import type { GameEvent, Habitat } from "../../engine/types";

const LAND: readonly Habitat[] = ["forest", "grassland", "wetland", "mountain", "desert", "urban", "cave"];
const WATER: readonly Habitat[] = ["sea", "freshwater"];

export const COMMON_EVENTS: readonly GameEvent[] = [
  // ---------- อาหาร ----------
  {
    id: "food_found",
    stageTags: ["young", "adult", "old"],
    requires: { sessile: false },
    weight: 12,
    text: "คุณเจอแหล่งอาหารที่อุดมสมบูรณ์",
    choices: [
      {
        label: "กินให้อิ่มเต็มที่",
        outcomes: [
          { chance: 0.8, text: "คุณกินจนอิ่มแปล้", effects: { fullness: 35 } },
          { chance: 0.2, text: "คุณกินเพลินจนนักล่าตัวหนึ่งเริ่มสังเกตเห็น", effects: { fullness: 30, safety: -15 } },
        ],
      },
      {
        label: "กินพอประมาณแล้วรีบไป",
        outcomes: [{ chance: 1, text: "คุณกินเท่าที่จำเป็นแล้วหลบไปอย่างปลอดภัย", effects: { fullness: 20, safety: 5 } }],
      },
    ],
  },
  {
    id: "food_scarce",
    stageTags: ["young", "adult", "old"],
    requires: { sessile: false },
    weight: 6,
    text: "ช่วงนี้อาหารหายากกว่าปกติ",
    choices: [
      {
        label: "ออกหาไกลกว่าเดิม",
        outcomes: [
          { chance: 0.6, text: "การเดินทางคุ้มค่า คุณเจออาหารพอกิน", effects: { move: "near", fullness: 25, safety: -10 } },
          { chance: 0.4, text: "คุณเหนื่อยเปล่า และกลับมามือเปล่า", effects: { fullness: 5, health: -10 } },
        ],
      },
      {
        label: "เก็บแรงไว้ รอให้อาหารกลับมา",
        outcomes: [{ chance: 1, text: "คุณประหยัดพลังงาน แต่ท้องก็ร้องทั้งวัน", effects: { fullness: 5, health: 3 } }],
      },
    ],
  },

  // ---------- นักล่าและอันตราย ----------
  {
    id: "predator_near",
    stageTags: ["dependent", "young", "adult", "old"],
    requires: { sessile: false },
    weight: 8,
    text: "คุณรู้สึกได้ว่ามีนักล่าอยู่ใกล้ๆ",
    choices: [
      {
        label: "อยู่นิ่งๆ ซ่อนตัว",
        outcomes: [
          { chance: 0.75, text: "นักล่าผ่านไปโดยไม่เห็นคุณ", effects: { safety: 10 } },
          { chance: 0.25, text: "มันเจอคุณ! คุณหนีรอดมาได้แต่บาดเจ็บ", effects: { health: -30, addFlags: ["injured"], cause: "predator" } },
        ],
      },
      {
        label: "หนีสุดชีวิต",
        outcomes: [
          { chance: 0.6, text: "คุณหนีพ้นได้ทันเวลา", effects: { fullness: -10, safety: 5 } },
          { chance: 0.3, text: "คุณรอดมาได้ แต่ได้แผลกลับมาด้วย", effects: { health: -20, addFlags: ["injured"], cause: "predator" } },
          { chance: 0.1, text: "นักล่าเร็วกว่าที่คิด", effects: { death: "predator" } },
        ],
      },
    ],
  },
  {
    id: "injury",
    stageTags: ["dependent", "young", "adult", "old"],
    requires: { flags: ["injured"] },
    weight: 8,
    text: "แผลของคุณยังไม่หายดี",
    choices: [
      {
        label: "พักรักษาตัว",
        outcomes: [
          { chance: 0.7, text: "แผลค่อยๆ สมานจนหายดี", effects: { health: 20, fullness: -10, removeFlags: ["injured"] } },
          { chance: 0.3, text: "แผลหายช้ากว่าที่คิด", effects: { health: 5, fullness: -10 } },
        ],
      },
      {
        label: "ฝืนออกหาอาหาร",
        outcomes: [
          { chance: 0.5, text: "คุณได้อาหารมาแม้จะเจ็บ", effects: { fullness: 20 } },
          { chance: 0.5, text: "แผลอักเสบหนักขึ้น", effects: { health: -15, cause: "disease" } },
        ],
      },
    ],
  },

  // ---------- ธรรมชาติ ----------
  {
    id: "storm",
    stageTags: ["dependent", "young", "adult", "old"],
    requires: { sessile: false },
    weight: 3,
    text: "ท้องฟ้ามืดครึ้ม พายุใหญ่กำลังมา",
    choices: [
      {
        label: "รีบหาที่หลบ",
        outcomes: [
          { chance: 0.85, text: "คุณหลบอยู่จนพายุผ่านไป", effects: { safety: 5 } },
          { chance: 0.15, text: "ที่หลบไม่แข็งแรงพอ คุณบาดเจ็บเล็กน้อย", effects: { health: -15, cause: "disaster" } },
        ],
      },
      {
        label: "อยู่ที่เดิม",
        outcomes: [
          { chance: 0.55, text: "พายุผ่านไปโดยไม่เป็นอะไร" },
          { chance: 0.4, text: "พายุซัดคุณจนบาดเจ็บ", effects: { health: -25, cause: "disaster" } },
          { chance: 0.05, text: "พายุรุนแรงเกินกว่าจะรอด", effects: { death: "disaster" } },
        ],
      },
    ],
  },
  {
    id: "drought",
    stageTags: ["young", "adult", "old"],
    requires: { habitat: LAND },
    weight: 3,
    text: "ฝนไม่ตกมานาน แหล่งน้ำเริ่มแห้งขอด",
    choices: [
      {
        label: "ย้ายไปหาแหล่งน้ำใหม่",
        outcomes: [
          { chance: 0.7, text: "คุณเจอแหล่งน้ำใหม่หลังเดินทางไกล", effects: { move: "near", fullness: -10, safety: -10, health: 5 } },
          { chance: 0.3, text: "การเดินทางกลางแดดทำให้คุณหมดแรง", effects: { health: -20, cause: "disaster" } },
        ],
      },
      {
        label: "อดทนรอฝน",
        outcomes: [
          { chance: 0.5, text: "ฝนตกลงมาทันเวลา", effects: { health: -5 } },
          { chance: 0.5, text: "ฝนไม่มาเสียที ร่างกายขาดน้ำ", effects: { health: -25, cause: "disaster" } },
        ],
      },
    ],
  },
  {
    id: "sickness",
    stageTags: ["dependent", "young", "adult", "old"],
    weight: 3,
    text: "คุณรู้สึกไม่สบาย ตัวร้อนและอ่อนแรง",
    choices: [
      {
        label: "พักผ่อนให้มาก",
        outcomes: [
          { chance: 0.7, text: "พักไม่กี่วันก็กลับมาแข็งแรง", effects: { health: 10, fullness: -10 } },
          { chance: 0.3, text: "อาการยังไม่ดีขึ้น", effects: { health: -15, cause: "disease" } },
        ],
      },
      {
        label: "ฝืนใช้ชีวิตตามปกติ",
        outcomes: [
          { chance: 0.5, text: "ร่างกายสู้ไหว อาการค่อยๆ หายไปเอง" },
          { chance: 0.45, text: "อาการทรุดลง", effects: { health: -25, cause: "disease" } },
          { chance: 0.05, text: "เชื้อโรคลุกลามจนร่างกายรับไม่ไหว", effects: { death: "disease" } },
        ],
      },
    ],
  },
  {
    id: "parasite",
    stageTags: ["dependent", "young", "adult", "old"],
    weight: 2,
    text: "มีปรสิตตัวเล็กๆ เกาะอยู่ตามตัว",
    choices: [
      {
        label: "พยายามกำจัดออก",
        outcomes: [
          { chance: 0.6, text: "คุณกำจัดมันออกได้เกือบหมด", effects: { health: 5 } },
          { chance: 0.4, text: "มันเกาะแน่นเกินกว่าจะเอาออก", effects: { health: -10, cause: "disease" } },
        ],
      },
      {
        label: "ช่างมัน",
        outcomes: [{ chance: 1, text: "ปรสิตค่อยๆ ดูดพลังงานของคุณไป", effects: { health: -15, fullness: -5, cause: "disease" } }],
      },
    ],
  },
  {
    id: "rest_day",
    stageTags: ["young", "adult", "old"],
    weight: 4,
    text: "วันนี้อากาศดี ไม่มีอะไรน่ากังวล",
    choices: [
      { label: "พักผ่อนให้เต็มที่", outcomes: [{ chance: 1, text: "คุณได้พักจนร่างกายสดชื่น", effects: { health: 10 } }] },
      { label: "ใช้เวลาหาอาหารสำรอง", outcomes: [{ chance: 1, text: "คุณหาอาหารได้เพิ่มอีกนิด", effects: { fullness: 15 } }] },
    ],
  },

  // ---------- มนุษย์ (เจอบ่อยขึ้นถ้าใกล้สูญพันธุ์) ----------
  {
    id: "habitat_clearing",
    stageTags: ["young", "adult", "old"],
    requires: { sessile: false, habitat: ["forest", "grassland", "wetland"] },
    weight: 1.5,
    threatenedWeight: 3,
    text: "เสียงเครื่องจักรดังมาจากไม่ไกล มนุษย์กำลังถางพื้นที่ใกล้ถิ่นของคุณ",
    choices: [
      {
        label: "ย้ายหนีไปหาที่ใหม่",
        outcomes: [
          { chance: 0.7, text: "คุณหาที่อยู่ใหม่ได้ แม้จะไม่ดีเท่าเดิม", effects: { move: "near", safety: -10, fullness: -10 } },
          { chance: 0.3, text: "ที่ใหม่มีเจ้าถิ่นอยู่แล้ว คุณโดนไล่จนบาดเจ็บ", effects: { move: "near", health: -15, cause: "accident" } },
        ],
      },
      {
        label: "หลบอยู่ที่เดิม",
        outcomes: [
          { chance: 0.5, text: "พวกเขายังไม่มาถึงตรงนี้ แต่อีกไม่นานคงมา", effects: { safety: -20 } },
          { chance: 0.4, text: "ต้นไม้ล้มลงมาใกล้ตัว คุณหนีออกมาได้อย่างหวุดหวิด", effects: { health: -20, cause: "human" } },
          { chance: 0.1, text: "ไม่เหลือที่ให้ซ่อนอีกแล้ว", effects: { death: "human" } },
        ],
      },
    ],
  },
  {
    id: "road",
    stageTags: ["young", "adult", "old"],
    requires: { sessile: false, habitat: ["forest", "grassland", "urban", "wetland"], canFly: false },
    weight: 1.5,
    threatenedWeight: 2,
    text: "ข้างหน้าเป็นถนนที่มีรถวิ่งผ่าน อาหารอยู่อีกฝั่ง",
    choices: [
      {
        label: "ข้ามไป",
        outcomes: [
          { chance: 0.7, text: "คุณข้ามไปได้อย่างปลอดภัย และเจออาหาร", effects: { fullness: 25 } },
          { chance: 0.2, text: "รถเฉี่ยวผ่านไปอย่างเฉียดฉิว", effects: { health: -30, cause: "human" } },
          { chance: 0.1, text: "รถวิ่งมาเร็วเกินกว่าจะหลบทัน", effects: { death: "human" } },
        ],
      },
      { label: "อยู่ฝั่งนี้", outcomes: [{ chance: 1, text: "คุณหาอาหารแถวนี้แทน ได้ไม่มากนัก", effects: { fullness: 5 } }] },
    ],
  },
  {
    id: "fishing_net",
    stageTags: ["young", "adult", "old"],
    archetypes: ["FISH", "REPTILE", "MAMMAL"],
    requires: { sessile: false, habitat: WATER },
    weight: 2,
    threatenedWeight: 2,
    text: "อวนประมงผืนใหญ่กำลังถูกลากผ่านมา",
    choices: [
      {
        label: "ดำลงไปให้ลึกที่สุด",
        outcomes: [
          { chance: 0.7, text: "อวนผ่านเหนือหัวคุณไป", effects: { safety: 5 } },
          { chance: 0.2, text: "คุณติดอวนแต่ดิ้นหลุดมาได้ ตัวเต็มไปด้วยแผล", effects: { health: -25, cause: "human" } },
          { chance: 0.1, text: "อวนรัดตัวคุณแน่นจนดิ้นไม่หลุด", effects: { death: "human" } },
        ],
      },
      {
        label: "ว่ายหนีไปด้านข้าง",
        outcomes: [
          { chance: 0.65, text: "คุณหลบออกมาได้ทัน", effects: { fullness: -10 } },
          { chance: 0.25, text: "ครีบของคุณเกี่ยวกับตาข่ายจนบาดเจ็บ", effects: { health: -20, cause: "human" } },
          { chance: 0.1, text: "ไม่ทันแล้ว อวนกวาดคุณขึ้นไปพร้อมกับตัวอื่นๆ", effects: { death: "human" } },
        ],
      },
    ],
  },
  {
    id: "plastic",
    stageTags: ["young", "adult", "old"],
    requires: { habitat: WATER },
    weight: 1.5,
    text: "มีอะไรบางอย่างลอยอยู่ในน้ำ ดูคล้ายอาหาร",
    choices: [
      {
        label: "กินเลย",
        outcomes: [
          { chance: 0.4, text: "โชคดี มันคืออาหารจริงๆ", effects: { fullness: 15 } },
          { chance: 0.6, text: "มันคือถุงพลาสติก ท้องของคุณปั่นป่วนไปหลายวัน", effects: { health: -25, fullness: -10, cause: "human" } },
        ],
      },
      { label: "ไม่ไว้ใจ ปล่อยไป", outcomes: [{ chance: 1, text: "คุณว่ายผ่านมันไปอย่างระมัดระวัง" }] },
    ],
  },

  // ---------- ไข่และดักแด้ ----------
  {
    id: "dormant_predator",
    stageTags: ["dormant"],
    weight: 4,
    text: "มีบางอย่างเคลื่อนไหวอยู่ใกล้ๆ ตัวคุณ",
    choices: [
      {
        label: "ภาวนาให้มันไม่เห็น",
        outcomes: [
          { chance: 0.8, text: "มันเดินจากไปโดยไม่สนใจคุณ", effects: { safety: 5 } },
          { chance: 0.2, text: "มันพบคุณ และคุณกลายเป็นอาหารของมัน", effects: { death: "predator" } },
        ],
      },
    ],
  },
  {
    id: "dormant_weather",
    stageTags: ["dormant"],
    weight: 3,
    text: "อากาศรอบตัวเปลี่ยนแปลงกะทันหัน",
    choices: [
      {
        label: "รอให้ผ่านไป",
        outcomes: [
          { chance: 0.8, text: "คุณยังปลอดภัยดีอยู่ข้างใน" },
          { chance: 0.2, text: "อุณหภูมิที่เปลี่ยนไปทำให้ร่างกายข้างในอ่อนแอลง", effects: { health: -30, cause: "disaster" } },
        ],
      },
    ],
  },
  {
    id: "dormant_quiet",
    stageTags: ["dormant"],
    weight: 5,
    text: "คุณอยู่นิ่งๆ ร่างกายกำลังเติบโตอย่างเงียบเชียบ",
    choices: [{ label: "รอ", outcomes: [{ chance: 1, text: "ทุกอย่างเป็นไปตามธรรมชาติ", effects: { health: 5 } }] }],
  },

  // ---------- ครอบครัว ----------
  {
    id: "find_mate",
    stageTags: ["adult"],
    requires: { sessile: false, broadcastSpawn: false, notFlags: ["has_mate", "role_worker", "role_queen"] },
    weight: 16,
    text: "ถึงฤดูผสมพันธุ์แล้ว คุณรู้สึกอยากมีคู่",
    choices: [
      {
        label: "ออกตามหาคู่อย่างจริงจัง",
        outcomes: [
          { chance: 0.7, text: "คุณเจอคู่ที่ถูกใจ!", effects: { fullness: -10, addFlags: ["has_mate"] } },
          { chance: 0.2, text: "ปีนี้ยังไม่เจอใครที่ใช่", effects: { fullness: -15 } },
          { chance: 0.1, text: "คุณต้องสู้กับคู่แข่งจนบาดเจ็บ", effects: { health: -20, safety: -10, addFlags: ["injured"], cause: "accident" } },
        ],
      },
      {
        label: "รอให้คู่มาหาเอง",
        outcomes: [
          { chance: 0.35, text: "มีตัวหนึ่งเข้ามาทำความรู้จักกับคุณ", effects: { addFlags: ["has_mate"] } },
          { chance: 0.65, text: "รอนานจนฤดูผสมพันธุ์ผ่านไป" },
        ],
      },
    ],
  },
  {
    id: "lay_eggs",
    stageTags: ["adult"],
    requires: { sessile: false, broadcastSpawn: false, flags: ["has_mate"], reproduction: "eggs", notSubgroups: ["mollusc.cephalopod"] },
    weight: 30,
    text: "ถึงเวลาวางไข่แล้ว",
    choices: [
      {
        label: "หาที่ปลอดภัยที่สุดก่อน",
        outcomes: [
          { chance: 0.85, text: "คุณวางไข่ได้ {n} ฟองในที่ปลอดภัย", effects: { offspring: "clutch", fullness: -15, removeFlags: ["has_mate"], addFlags: ["parent"] } },
          { chance: 0.15, text: "ระหว่างหาที่ คุณเกือบถูกนักล่าจับได้ แต่ก็วางไข่ได้ {n} ฟอง", effects: { offspring: "clutch", safety: -15, removeFlags: ["has_mate"], addFlags: ["parent"] } },
        ],
      },
      {
        label: "วางตรงนี้เลย",
        outcomes: [{ chance: 1, text: "คุณวางไข่ {n} ฟอง แล้วหวังว่าพวกมันจะรอด", effects: { offspring: "clutch", fullness: -5, removeFlags: ["has_mate"], addFlags: ["parent"] } }],
      },
    ],
  },
  {
    id: "give_birth",
    stageTags: ["adult"],
    requires: { sessile: false, broadcastSpawn: false, flags: ["has_mate"], reproduction: "live", notSubgroups: ["arachnid.scorpion"] },
    weight: 30,
    text: "คุณตั้งท้องและใกล้คลอดแล้ว",
    choices: [
      {
        label: "หาที่คลอดที่ซ่อนตัวมิดชิด",
        outcomes: [{ chance: 1, text: "คุณคลอดลูก {n} ตัวอย่างปลอดภัย", effects: { offspring: "clutch", fullness: -20, removeFlags: ["has_mate"], addFlags: ["parent"] } }],
      },
      {
        label: "อยู่กับฝูงให้ช่วยกันระวังภัย",
        outcomes: [
          { chance: 0.8, text: "คุณคลอดลูก {n} ตัวท่ามกลางฝูง", effects: { offspring: "clutch", fullness: -10, removeFlags: ["has_mate"], addFlags: ["parent"] } },
          { chance: 0.2, text: "การคลอดยากกว่าที่คิด แต่ลูก {n} ตัวก็ปลอดภัย", effects: { offspring: "clutch", health: -20, removeFlags: ["has_mate"], addFlags: ["parent"], cause: "disease" } },
        ],
      },
    ],
  },
  {
    id: "growing_old",
    stageTags: ["old"],
    weight: 5,
    text: "ร่างกายของคุณไม่คล่องแคล่วเหมือนเดิมแล้ว",
    choices: [
      { label: "ใช้ชีวิตช้าๆ อย่างระวัง", outcomes: [{ chance: 1, text: "คุณรู้จักถิ่นนี้ดีกว่าใคร จึงหลบอันตรายได้เก่ง", effects: { health: -5, safety: 10 } }] },
      {
        label: "ฝืนทำเหมือนตอนยังหนุ่มสาว",
        outcomes: [
          { chance: 0.5, text: "คุณยังทำได้! ได้อาหารมาไม่น้อย", effects: { fullness: 20 } },
          { chance: 0.5, text: "ร่างกายไม่ไหว คุณบาดเจ็บ", effects: { health: -20, cause: "accident" } },
        ],
      },
    ],
  },
];
